// ════════════════════════════════════════════════════════════
//  online/bracket.js — THE BRACKET (PURE): any tournament, any night → a tree you can follow
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-22: "I want a real deal tournament bracket I can follow for all tourneys big or small cup or not … see the
//  progress of the night, see who they fight, who they fight if they win, etc … if there's real deal eliminations progress to
//  fight till the end, yeah let's get a real bracket to track that".
//
//  Nothing here decides anything: the cups' rules and fields are circuit.js (cupPlan / apocalypsePlan); this only READS them into
//  a shape a screen can draw — rounds of matches, each side a fighter or "the winner of …" — and answers the two questions a
//  fan has: "what happened to X, and who's next?" (storyOf) and "how is the night going?" (nightOf).
//
//  A match (node) is one of:
//    done     fought — winner known (row = the record: how long, how it ended)
//    live     on air right now          ringside   at ringside, bell not yet rung
//    ready    both fighters known, still to come   waiting   a fighter is still to be decided (a side says who feeds it)
//    bye      one empty seat — the other fighter walks through     empty   nobody (a short field)
// ════════════════════════════════════════════════════════════
import {
  cupPlan, cupsOfDay, cupSlotHour, CUPS, roundsOf, finalR, roundName, hourInfo, hourStart, hoursOfDay, apocalypsePlan, kindHours, weekOfDay, racingDayOf,
  cleanRows, nameOfKey,
} from "./circuit.js";

export const ROUND_LABEL = { "round of 16": "Round of 16", quarterfinal: "Quarterfinals", semifinal: "Semifinals", final: "Final" };
const ROUND_ONE = { "round of 16": "Round of 16", quarterfinal: "Quarterfinal", semifinal: "Semifinal", final: "Final" };
export const roundTitle = (c, r, one = false) => (one ? ROUND_ONE : ROUND_LABEL)[roundName(c, r)] || "";

/** What a cup's tag says on a button (the cup nights, Salvation, the rest). */
export const CUP_SHORT = { q1: "Circuit Qualifier I", q2: "Contenders Qualifier I", q3: "Circuit Qualifier II", q4: "Contenders Qualifier II", bb: "Bottom vs Bottom", kc: "Contenders Cup", lc: "Last Chance", tt: "Top vs Top", c: "Circuit Cup", s: "Salvation", l: "Losers' Cup", f: "Season Finale", k: "Contenders Cup", x: "Champions Cup", w: "Winners' Cup" };
export const cupShort = (c) => CUP_SHORT[c] || (CUPS[c] ? CUPS[c].name.replace(/^the /, "") : "Cup");

const isKey = (x) => typeof x === "string";

/** The fight (if any) the stage is running for cup bout (c, r, i) on racing day `day`: fights = the stage's bouts (stage.js). */
function fightOf(fights, c, r, i, day) {
  return (fights || []).find((f) => f && f.k === "cup" && f.c === c && f.r === r && f.i === i && (day == null || racingDayOf(hourStart(f.h)) === day)) || null;
}

/**
 * A cup's whole bracket on a racing day.
 *   ctx = { league, weekRows, seasonRows?, entered? } (the same ctx cupPlan reads — circuitStore's snapshot)
 *   fights = the stage's bouts (loadStage: prev + cur + next), for "live" and the bell times; optional.
 * Resolves { c, day, name, size, na, rounds:[{ r, name, nodes:[node] }], champion, played, total, roundNow, final: node }
 * node = { id, c, r, i, state, a, b, winner, loser, row, fight, hour, when:{ms,kind:"bell"|"about"|"hour"}|null, feeds:{r,i}|null }
 * side = { key|null, pending, from:{r,i}|null, seed:"3"|"C9"|null, div:"c"|"k"|null }
 */
export function bracketOf(c, day, ctx, fights = []) {
  const week = weekOfDay(day), size = CUPS[c].size, R = roundsOf(c);
  const P = cupPlan(c, week, ctx, day);
  const rows = cleanRows((ctx && ctx.weekRows) || []).filter((r) => r.kind === "cup" && r.c === c && (P.day == null || racingDayOf(hourStart(r.h)) === P.day));
  const field = P.field || [];
  // a mixed cup (two divisions' halves) numbers each division on its own: C9, K1 …
  const mixed = P.na != null && CUPS[c].sides && CUPS[c].sides.length === 2;
  const offset = (side) => (side === "cb" || side === "kb" ? 8 : 0);
  const seedOf = (k) => {
    const x = field.indexOf(k);
    if (x < 0) return { seed: null, div: null };
    if (!mixed) return { seed: String(x + 1), div: null };
    const half = x < P.na ? 0 : 1, rank = (half ? x - P.na : x) + offset(CUPS[c].sides[half]);
    return { seed: `${half ? "K" : "C"}${rank + 1}`, div: half ? "k" : "c" };
  };
  const side = (k, r, i, s) => {
    if (isKey(k)) return { key: k, pending: false, from: null, ...seedOf(k) };
    if (k === null) return { key: null, pending: false, from: null, seed: null, div: null };   // an empty seat
    return { key: null, pending: true, from: { r: r - 1, i: 2 * i + s }, seed: null, div: null };   // the bout before isn't decided
  };
  const rounds = [];
  for (let r = 0; r < R; r++) {
    const nodes = [];
    for (let i = 0; i < size >> (r + 1); i++) {
      const sd = P.sides(r, i);
      // a seat whose feeder bout the STAGE has seen end (the record's row is still on its way) already holds the winner
      const upgrade = (x) => { if (x.pending) { const fd = rounds[r - 1].nodes[x.from.i]; if (fd && fd.winner) return { key: fd.winner, pending: false, from: null, ...seedOf(fd.winner) }; } return x; };
      const a = upgrade(side(sd.a, r, i, 0)), b = upgrade(side(sd.b, r, i, 1));
      const f = fightOf(fights, c, r, i, P.day), row = rows.find((x) => x.r === r && x.i === i && ((x.a === a.key && x.b === b.key) || (x.a === b.key && x.b === a.key))) || null;
      let state, winner = null, loser = null;
      if (a.key && b.key) {
        winner = isKey(P.winner(r, i)) ? P.winner(r, i) : null;
        if (!winner && f && f.phase === "done" && (f.winner === 0 || f.winner === 1)) winner = f.winner === 0 ? f.A.key : f.B.key;   // the stage has seen it end; the record is on its way
        loser = winner ? (winner === a.key ? b.key : a.key) : null;
        state = winner ? "done" : f && f.phase === "live" ? "live" : f && f.phase === "prep" ? "ringside" : "ready";
      } else if ((a.key && b.key === null && !b.pending) || (b.key && a.key === null && !a.pending)) { state = "bye"; winner = a.key || b.key; }
      else if (!a.pending && !b.pending) state = "empty";
      else state = "waiting";
      const hour = cupSlotHour(c, r, i, day);
      const at = f ? (f.startAt != null ? { ms: f.startAt, kind: "bell" } : f.estAt != null ? { ms: f.estAt, kind: "about" } : null) : null;
      nodes.push({ id: `${c}:${r}:${i}`, c, r, i, state, a, b, winner, loser, row, fight: f, hour, when: at || (hour != null ? { ms: hourStart(hour), kind: "hour" } : null), feeds: r + 1 < R ? { r: r + 1, i: i >> 1 } : null });
    }
    rounds.push({ r, name: roundTitle(c, r), one: roundTitle(c, r, true), nodes });
  }
  const real = rounds.flatMap((x) => x.nodes).filter((n) => n.state !== "bye" && n.state !== "empty");
  const played = real.filter((n) => n.state === "done").length;
  const roundNow = rounds.findIndex((x) => x.nodes.some((n) => n.state !== "done" && n.state !== "bye" && n.state !== "empty"));
  return { c, day, name: CUPS[c].name.replace(/^the /, "The "), short: cupShort(c), size, na: P.na ?? null, mixed, rounds, champion: isKey(P.champion) ? P.champion : null, played, total: real.length, roundNow: roundNow < 0 ? R : roundNow, final: rounds[R - 1].nodes[0] };
}

/** A node by (r, i). */
export const nodeAt = (M, r, i) => (M.rounds[r] && M.rounds[r].nodes[i]) || null;
/** Who is on a side, as a short phrase for a "waiting" seat: the two names of the bout that feeds it ("Mira or Tal"). */
export function feederOf(M, sd) {
  if (!sd.from) return null;
  const n = nodeAt(M, sd.from.r, sd.from.i);
  if (!n) return null;
  return { node: n, names: [n.a.key, n.b.key] };
}

/**
 * A fighter's road through a cup: what happened, what's next, and — if they win — who is waiting (or which two bouts decide it).
 * Resolves null if the key isn't in the cup. { key, seed, out (lost), champion, outAt (round), items:[ … ] } with items in order:
 *   { kind:"won"|"lost"|"bye", node, opp, ... }                   — fought
 *   { kind:"live"|"ringside"|"next", node, opp|null, oppFrom }    — the bout they're in now / next (opp null: still to be decided)
 *   { kind:"ifwin", node, opp|null, oppFrom }                     — every round after, if they keep winning
 */
export function storyOf(M, key) {
  let first = null;
  for (const rd of M.rounds) { const n = rd.nodes.find((x) => x.a.key === key || x.b.key === key); if (n) { first = n; break; } }
  if (!first) return null;
  const items = [];
  // (a round they haven't reached yet: their seat is the one the bout they're in feeds)
  const view = (n, prev) => {
    const mine = n.a.key === key ? 0 : n.b.key === key ? 1 : prev && n.a.from && n.a.from.r === prev.r && n.a.from.i === prev.i ? 0 : 1;
    const me = mine ? n.b : n.a, other = mine ? n.a : n.b;
    return { node: n, me, opp: other.key, oppFrom: other.pending ? feederOf(M, other) : null, oppEmpty: !other.key && !other.pending };
  };
  let n = first, prev = null, out = false, champion = false, outAt = null, upcoming = false;
  while (n) {
    const v = view(n, prev);
    if (n.state === "done") {
      const won = n.winner === key;
      items.push({ ...v, kind: won ? "won" : "lost" });
      if (!won) { out = true; outAt = n.r; break; }
    } else if (n.state === "bye") items.push({ ...v, kind: "bye" });
    else {
      items.push({ ...v, kind: upcoming ? "ifwin" : n.state === "live" ? "live" : n.state === "ringside" ? "ringside" : "next" });
      upcoming = true;
    }
    if (!n.feeds) { champion = n.winner === key; break; }
    prev = n;
    n = nodeAt(M, n.feeds.r, n.feeds.i);
  }
  return { key, seed: first.a.key === key ? first.a.seed : first.b.seed, out, outAt, champion, items };
}

/** The cups fought on a racing day — V3's, V2's, or (the first week, before them) Thursday's Losers' Cup, Saturday's Winners' Cup, the Finale. */
export function cupsOn(day) {
  const c = cupsOfDay(day);
  if (c.length) return c;
  const out = [];
  for (const h of hoursOfDay(day)) { const k = hourInfo(h).kind, x = k === "losers" ? "l" : k === "winners" ? "w" : k === "finale" ? "f" : null; if (x && !out.includes(x)) out.push(x); }
  return out;
}
/**
 * One racing day's tournaments, for the night strip: every cup with how far it has got, the next thing to watch for.
 * ctx/fights as bracketOf. Resolves [{ c, M, status:"done"|"live"|"soon"|"later", line, finalHour }] in the order the finals are fought.
 */
export function nightOf(day, ctx, fights = [], now = Date.now()) {
  const out = cupsOn(day).map((c) => {
    const M = bracketOf(c, day, ctx, fights);
    const finalHour = cupSlotHour(c, M.rounds.length - 1, 0, day);
    const live = M.rounds.flatMap((x) => x.nodes).some((n) => n.state === "live" || n.state === "ringside");
    const rd = M.rounds[Math.min(M.roundNow, M.rounds.length - 1)];
    const left = rd.nodes.filter((n) => n.state !== "done" && n.state !== "bye" && n.state !== "empty").length;
    let status, line;
    if (M.champion) { status = "done"; line = `Won by ${nameOfKey(M.champion)}`; }
    else if (M.played === 0 && !live) { status = finalHour != null && hourStart(finalHour) + 3600e3 < now ? "done" : "later"; line = status === "later" ? "Draws at the cut" : "Not fought"; }
    else {
      status = live ? "live" : "soon";
      line = `${rd.one || rd.name}${rd.nodes.length > 1 ? ` · ${rd.nodes.length - left} of ${rd.nodes.filter((n) => n.state !== "bye" && n.state !== "empty").length} played` : ""}`;
    }
    return { c, M, status, line, finalHour };
  });
  return out.sort((x, y) => (x.finalHour ?? 1e9) - (y.finalHour ?? 1e9));
}

/**
 * Friday's Apocalypse (best of 3, the Circuit's bottom vs the Contenders' top) as a bracket of series.
 * Resolves { series:[{ i, a, b, wins:[n,n], done, winner, games:[{ g, hour, state:"done"|"live"|"ringside"|"next"|"maybe"|"skipped", winner, row, when }] }], safe, hours }
 */
export function apoOf(week, ctx, fights = []) {
  const P = apocalypsePlan(ctx.league, cleanRows(ctx.weekRows || []), week);
  const hours = kindHours(week, "apocalypse");
  const rows = cleanRows(ctx.weekRows || []).filter((r) => r.kind === "apo");
  const series = P.series.map((s) => {
    let a = 0, b = 0;
    const games = hours.map((h, g) => {
      const row = rows.find((x) => x.i === s.i && x.g === g && x.a === s.a && x.b === s.b) || null;
      const f = (fights || []).find((x) => x && x.k === "apo" && x.i === s.i && x.g === g && x.h === h) || null;
      let state, winner = null;
      if (row) { state = "done"; winner = row.w === 0 ? row.a : row.b; if (winner === s.a) a++; else b++; }
      else if (f && f.phase === "live") state = "live";
      else if (f && f.phase === "prep") state = "ringside";
      else if (s.done) state = "skipped";
      else if (g < 2) state = "next";
      else state = "maybe";   // game 3 only if it's 1–1
      return { g, hour: h, state, winner, row, when: f && f.startAt != null ? { ms: f.startAt, kind: "bell" } : f && f.estAt != null ? { ms: f.estAt, kind: "about" } : { ms: hourStart(h), kind: "hour" } };
    });
    // the first game still to come is "next"; the others after it only "maybe"
    const nextG = games.findIndex((x) => x.state === "next");
    games.forEach((x, g) => { if (x.state === "next" && g > nextG) x.state = "maybe"; });
    return { ...s, wins: [a, b], games };
  });
  return { series, safe: P.safe, hours, drop: P.drop, rise: P.rise };
}

/** The racing days of a week that have a tournament, in order: [{ day, cups, apo }]. */
export function daysOf(week) {
  const out = [];
  for (let d = week * 7 - 4; d < week * 7 + 3; d++) {
    const hs = hoursOfDay(d), cups = cupsOn(d), apo = hs.some((h) => hourInfo(h).kind === "apocalypse");
    if (cups.length || apo) out.push({ day: d, cups, apo });
  }
  return out;
}
export { finalR };
